import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { CURRENT_STATE_PROJECT_KEY, EDGE_TYPE, getInsights, INSIGHTS_FIELD, isSupplyChain, LAYOUTS, Linqset, MODEL_TYPE, NODE_SUPERTYPE, ORGANISATION_MODEL_KEY } from "../../common/linqset-model";
import { createLinqset } from "../../common/model-processing";
import { LinqsetRepositoryService } from "../../model-services/linqset-repository.service";
import { ActivatedRoute, Router } from "@angular/router";
import { GraphComponentComponent } from "../../canvas/graph-component/graph-component.component";
import { AdjacencyTypes, Arrow, ArrowType, EdgeStyleIndicatorRenderer, FilteredGraphWrapper, HierarchicalLayout, IEdge, IGraph, IModelItem, INode, LongestPath, NodeStyleIndicatorRenderer, PolylineEdgeStyle, ResultItemCollection, ShapeNodeStyle, Stroke, TimeSpan } from "@yfiles/yfiles";
import { TemporalService } from "../../temporal/temporal.service";
import { LAYOUT_DIRECTION, User, USER_ROLE } from "../../common/user";
import { GraphService } from "../../canvas/graph.service";
import { LabelService } from "../../model-services/label.service";
import { InsightsFieldType } from "../../insights/model/insights-field-type";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { getColourForSuperType, IMAGE_LINQ } from "../../model-services/model-item-enums";
import { MatSidenav } from "@angular/material/sidenav";
import { SCREEN } from "../layouts-enums";
import { UserService } from "../../user/user.service";
import { MatDialog } from "@angular/material/dialog";
import { createInsightsFilter, modelChangedInProject, ModelDetails, Organisation } from "../../common/organisation";
import { OrganisationService } from "../../organisation/organisation.service";
import { environment } from "../../../environments/environment";
import { MatSnackBar } from "@angular/material/snack-bar";
// names come from the model, which people can edit, so they are escaped before going into the messages' html
import { escapeHtml } from "../../linq-utils/formatting/html";
import { FUNCTION, FunctionService } from "../../model-services/function.service";
import { AppComponent } from "../../app.component";
import { RegisterDialogComponent } from "../../user/unauthenticated/register-dialog-component";
import { CREATE_ORGANISATION_URL, DEFAULT_URL } from "../../app-urls";
import { inviteToCreateOrganisation } from "../../user/onboarding";
import { SECONDS_PER_YEAR } from "../../temporal/simulation-run";
import { WORKING_SECONDS_PER_DAY } from "../../temporal/simulation-report";

// the brand magenta, so the step being described stands out without reading as an error
const HIGHLIGHT_COLOUR = '#D6006E'


// Predicates to determine which items to hide
const nodePredicate = (node: INode): boolean => !node.tag || !node.tag.filtered
const edgePredicate = (edge: IEdge): boolean => !edge.tag || !edge.tag.filtered

enum INTRODUCTION_STAGE {
    INITIAL,
    ORGANISATION,
    START,
    NEXT,
    ANIMATION,
    HEATMAP,
    EPM,
    PROJECT,
    FINISH
}

// the phases shown in the guide's progress bar, each covering one or more stages. The bar has room for a short name;
// the line above the title uses the label
interface IntroductionPhase {
    label:string
    short:string
    stages:INTRODUCTION_STAGE[]
}

// a part of the EPM scorecard the introduction stops at; anchor is its data-tour name in the scorecard
interface EpmTourStep {
    anchor:string
    title:string
    message:string
}

// in the order they appear in the scorecard. A part the scorecard doesn't show for the model is skipped
const EPM_TOUR:EpmTourStep[] = [
    {anchor:'overview', title:'The EPM scorecard', message:'Economic Process Modelling turns the model into an economic report: where value is created and where it is lost. Figures tagged <b>grounded</b> are read straight from the model, such as volumes, durations, costs and capacity. Those tagged <b>indicative</b> are inferred from its shape, such as risk and information value.'},
    {anchor:'summary', title:'The story in a paragraph', message:'The summary pulls the main findings together: what the process produces, what it costs to run and where the biggest drag on value sits. It is the place to start reading.'},
    {anchor:'capacity', title:'Capacity', message:"The gauge compares the demand on the busiest role with the hours its people have. Above 100% the role can't keep up, so work queues, as the simulation showed."},
    {anchor:'headlines', title:'Headlines', message:'The figures that matter most at a glance. Each says whether it is grounded or indicative, and once a future state is chosen each shows its change.'},
    {anchor:'profit-and-loss', title:'Economic P&L', message:'The flow over a year, derived from the model: the revenue and value the process creates against the cost of the people and systems doing the work, and how much of that work is friction.'},
    {anchor:'balance-sheet', title:'Economic balance sheet', message:'Position and exposure rather than flow: the capacity the process holds, the risk it carries and the information it depends on, each in its own units rather than forced into money.'},
    {anchor:'ledger', title:'Value vs drag', message:'A balanced picture rather than a single score: what adds value on one side, and what holds it back on the other, such as friction, rework, manual bridges between systems and risk.'},
    {anchor:'value-profile', title:'Economic value profile', message:'What each model contributes. Bars above the line are value created and below it value destroyed; the hatched cap is value produced but not yet realised.'},
    {anchor:'lenses', title:'Drill in', message:'The ranked contributors to act on first. Switch between the <b>friction</b>, <b>risk</b> and <b>information</b> lenses to see which steps, roles, systems and outputs drive each.'},
    {anchor:'decisions', title:'Decisions', message:'What the scorecard suggests doing about it, prioritised and sequenced. Gates come first: what to settle before the rest is worth doing.'},
    {anchor:'data-gaps', title:'Data gaps', message:"What the model doesn't carry yet. Rather than inventing a figure, the report says where data is missing, so the model can be completed and the scorecard sharpened."},
]

@Component({
    selector: 'app-introduction-layout',
    templateUrl: './introduction-layout.html',
    styleUrls: ['./introduction-layout.css'],
    standalone: false
})
export class IntroductionLayoutComponent implements AfterViewInit, OnDestroy {
    screen = SCREEN.MODEL_INTRODUCTION
    loading:boolean = true
    modelKey:string = undefined
    comparisonKey:string = undefined

    linqset:Linqset
    user:User
    comparison:Linqset

    joined:Linqset

    routeSub:Subscription
    querySub:Subscription

    currentNode:INode
    longestPathEdges:ResultItemCollection<IEdge>
    title:string = ""
    safeMessages:SafeHtml[] = []
    stage:INTRODUCTION_STAGE = INTRODUCTION_STAGE.INITIAL
    // which action of the step through is showing, counting from 1
    step:number = 0
    // whether the model has a future state project to compare, so the walk through has a projects stage
    hasProjectStage:boolean = false
    // how many steps the step through has: the actions on the main path, and the input when it comes from another model
    totalSteps:number = 0
    // the node the step through last centred on, to centre on again after going back
    focusNode:INode
    // going back replays the walk through to the earlier stage; while it does, nothing is animated or laid out
    replaying:boolean = false
    navigating:boolean = false

    // how far the simulation has got, refreshed while it shows
    simulation:{day:number, days:number, time:string, percent:number, received:number, completed:number, queued:number}
    simulationRunning:boolean = false
    simulationFinished:boolean = false
    private simulationTimer:number

    // the organisation whose models a visitor chooses from, and those models, for those who start at the organisation
    // model rather than at a model
    organisation:Organisation
    organisationModels:ModelDetails[] = []

    // the graphs built for each model loaded, and when the latest is ready
    private graphBuilt = new Subject<Linqset>()
    private graphShown:Promise<void> = Promise.resolve()

    // the model the EPM report scores. It is set the first time the report is reached and kept, so the report isn't rebuilt
    // each time the walk through comes back to it
    epmLinqset:Linqset
    // the parts of the report the walk through stops at, once the report has been built, and which is showing
    epmSteps:EpmTourStep[] = []
    epmIndex:number = 0
    epmReady:boolean = false
    @ViewChild('epmReport') epmReport:ElementRef<HTMLElement>

    websiteUrl = environment.websiteUrl
    demoBookingUrl = environment.demoBookingUrl

    inputs: {node:INode, itemsPerYear:number}[]

    modelType:MODEL_TYPE = MODEL_TYPE.PROCESS
    fullGraph:IGraph
    filteredGraph:FilteredGraphWrapper
    INTRODUCTION_STAGE=INTRODUCTION_STAGE
    IMAGE_LINQ = IMAGE_LINQ
    USER_ROLE = USER_ROLE

    orientation:LAYOUT_DIRECTION

    @ViewChild(GraphComponentComponent) gcComponent: GraphComponentComponent
    @ViewChild('leftdrawer') public leftDrawer: MatSidenav;

    constructor(private route:ActivatedRoute, private linqsetRepository:LinqsetRepositoryService, private userService:UserService, private router:Router,
        private dialog:MatDialog, private temporalService:TemporalService, private sanitizer: DomSanitizer, public app:AppComponent,
        private graphService:GraphService, private labelService:LabelService, private organisationService:OrganisationService, private functionService:FunctionService,
        private snackBar:MatSnackBar) {}

    ngOnDestroy(): void {
        if (this.routeSub) this.routeSub.unsubscribe()
        if (this.querySub) this.querySub.unsubscribe()
        this.stopSimulationProgress()
        this.dialog.closeAll()
    }

    async ngAfterViewInit(): Promise<void> {
        this.labelService.nodeInsightsField = INSIGHTS_FIELD.ANNUAL_HOURS

        setTimeout(async () => {
            this.user = this.userService.isLoggedIn()?this.userService.getUser():undefined
            if (!this.user) this.user = await this.userService.signInGuest()

            const preferences = this.userService.getUserPreferences()
            this.orientation = preferences?.graphDirection||LAYOUT_DIRECTION.DOWN

            const {graphComponent} = this.gcComponent

            if (!this.routeSub) {
                this.routeSub = this.route.params.subscribe(async p => {
                    this.stage = INTRODUCTION_STAGE.INITIAL
                    const organisationKey = p['organisation_key']
                    this.comparison = undefined
                    this.comparisonKey = undefined
                    this.modelKey = undefined

                    if (organisationKey && organisationKey.length > 0) {
                        this.modelKey = p['model_key']
                        const projectKey = p['project_key']
                        this.user = this.userService.isLoggedIn()?this.userService.getUser():undefined
                        if (!this.user || this.user.role === USER_ROLE.GUEST) {
                            this.router.navigateByUrl(DEFAULT_URL(organisationKey))
                            return
                        }

                        if (!this.modelKey) {
                            // the walkthrough of the organisation's own models, starting at its organisation model. The
                            // project compared is the one it was opened from, or else the organisation's first
                            const organisation = await this.organisationService.getOrganisation(organisationKey)
                            this.comparisonKey = this.route.snapshot.queryParamMap.get('comparisonKey') || organisation?.projects?.[0]?._key
                            this.organisation = organisation
                            this.showOrganisation()
                            return
                        }

                        if (projectKey === CURRENT_STATE_PROJECT_KEY)
                            this.comparisonKey = this.route.snapshot.queryParamMap.get('comparisonKey')
                        else this.comparisonKey = CURRENT_STATE_PROJECT_KEY
                        if (await this.loadModelData(organisationKey, this.modelKey, projectKey)) await this.beginModel()
                    }else {
                        const organisation = await this.organisationService.getOrganisation(environment.demoOrganisationKey)
                        this.comparisonKey = organisation?.projects?.[0]?._key
                        this.organisation = organisation
                        this.showOrganisation()
                    }
                })
            }

            // A filtered Graph which wraps the original graph and applies the predicates
            this.filteredGraph = new FilteredGraphWrapper(graphComponent.graph, nodePredicate, edgePredicate)
            // fullGraph is a reference to the original, unfiltered graph wrapped by filteredGraph
            this.fullGraph = this.filteredGraph.wrappedGraph!
            this.fullGraph.tag = {orientation: this.orientation}

            // Assign the filtered graph to the graph component for display
            graphComponent.graph = this.filteredGraph

            graphComponent.graph.decorator.nodes.highlightRenderer.addConstant(
                new NodeStyleIndicatorRenderer({
                    nodeStyle: new ShapeNodeStyle({ shape: 'rectangle', fill: 'none', stroke: new Stroke({fill:HIGHLIGHT_COLOUR, thickness: 2 })}),
                    margins: 0
                })
            )
            graphComponent.graph.decorator.edges.highlightRenderer.addConstant(
                new EdgeStyleIndicatorRenderer({
                    edgeStyle: new PolylineEdgeStyle({stroke:new Stroke({fill:HIGHLIGHT_COLOUR, thickness: 2 }), targetArrow:new Arrow(ArrowType.TRIANGLE, HIGHLIGHT_COLOUR, new Stroke({fill:HIGHLIGHT_COLOUR, thickness: 1 }))})
                })
            )
        }, 0)
    }

    // whether the walk through starts at the organisation model, for a visitor choosing a model there, rather than at a model
    get startsAtOrganisation():boolean {
        return !this.modelKey
    }

    get phases():IntroductionPhase[] {
        const phases:IntroductionPhase[] = []
        if (this.startsAtOrganisation) phases.push({label:'Organisation model', short:'Models', stages:[INTRODUCTION_STAGE.ORGANISATION]})
        phases.push(
            {label:'Step through', short:'Steps', stages:[INTRODUCTION_STAGE.START, INTRODUCTION_STAGE.NEXT]},
            {label:'Simulation', short:'Simulate', stages:[INTRODUCTION_STAGE.ANIMATION]},
            {label:'Heatmap', short:'Heatmap', stages:[INTRODUCTION_STAGE.HEATMAP]}
        )
        phases.push({label:'EPM report', short:'EPM', stages:[INTRODUCTION_STAGE.EPM]})
        if (this.hasProjectStage) phases.push({label:'Future state', short:'Future', stages:[INTRODUCTION_STAGE.PROJECT]})
        phases.push({label:'Finish', short:'Finish', stages:[INTRODUCTION_STAGE.FINISH]})
        return phases
    }

    get phaseIndex():number {
        return this.phases.findIndex(p => p.stages.includes(this.stage))
    }

    get isWalkthrough():boolean {
        return this.stage === INTRODUCTION_STAGE.START || this.stage === INTRODUCTION_STAGE.NEXT
    }

    // what the guide is about: the organisation while its models are shown, then the model chosen
    get contextName():string|undefined {
        return this.stage === INTRODUCTION_STAGE.ORGANISATION ? this.organisation?.name : this.linqset?.model?.name
    }

    get eyebrow():string {
        // the count is worked out from the main path, so it is kept from falling behind the step showing
        if (this.isWalkthrough) return `Step ${this.step} of ${Math.max(this.totalSteps, this.step)}`
        if (this.stage === INTRODUCTION_STAGE.EPM && this.epmReady && this.epmSteps.length) return `EPM report · ${this.epmIndex + 1} of ${this.epmSteps.length}`
        return this.phases[this.phaseIndex]?.label || ''
    }

    get canGoBack():boolean {
        switch (this.stage) {
            // the first step goes back to choosing a model, for those who chose one
            case INTRODUCTION_STAGE.START:
                return this.startsAtOrganisation && !this.navigating
            case INTRODUCTION_STAGE.NEXT:
            case INTRODUCTION_STAGE.ANIMATION:
            case INTRODUCTION_STAGE.HEATMAP:
            case INTRODUCTION_STAGE.EPM:
            case INTRODUCTION_STAGE.PROJECT:
            case INTRODUCTION_STAGE.FINISH:
                return !this.navigating
            default:
                return false
        }
    }

    // the arrow keys step forwards and back, unless someone is typing or a dialog is open
    @HostListener('document:keydown', ['$event'])
    handleKeydown(event:KeyboardEvent) {
        // a model is chosen from the organisation model by double-clicking it, so the arrows have nothing to do there
        if (this.stage === INTRODUCTION_STAGE.INITIAL || this.stage === INTRODUCTION_STAGE.ORGANISATION || this.navigating || this.dialog.openDialogs.length > 0) return
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
        const target = event.target as HTMLElement
        if (target?.closest?.('input, textarea, select, [contenteditable="true"]')) return

        if (event.key === 'ArrowRight' && this.stage !== INTRODUCTION_STAGE.FINISH) {
            event.preventDefault()
            this.loadNextStage()
        }else if (event.key === 'ArrowLeft' && this.canGoBack) {
            event.preventDefault()
            this.back()
        }
    }

    // says where Next goes from here, so it reads as the action it is
    get nextLabel():string {
        switch (this.stage) {
            case INTRODUCTION_STAGE.START:
            case INTRODUCTION_STAGE.NEXT:
                return this.currentNode?.tag?.node ? 'Next step' : 'Run simulation'
            case INTRODUCTION_STAGE.ANIMATION:
                return 'Show heatmap'
            case INTRODUCTION_STAGE.HEATMAP:
                return 'See the EPM report'
            case INTRODUCTION_STAGE.EPM:
                if (this.epmIndex < this.epmSteps.length - 1) return 'Next'
                return this.hasProjectStage ? 'Compare future state' : 'Finish'
            default:
                return 'Finish'
        }
    }

    // a new user who walked through the demo organisation is invited to create their own at the end
    get inviteToCreate():boolean {
        return inviteToCreateOrganisation(this.user, this.organisation?._key, environment.demoOrganisationKey)
    }

    createOwnOrganisation() {
        this.router.navigateByUrl(CREATE_ORGANISATION_URL())
    }

    // where a visitor starts: the organisation's models, joined where information made in one is used in another, for
    // them to choose one to be walked through
    private async showOrganisation() {
        if (!this.organisation) {
            this.snackBar.open("The example models couldn't be loaded. Please try again later.", "close")
            return
        }

        this.loading = true
        try {
            const linqset = await this.linqsetRepository.loadOrganisationHomeData(this.organisation._key, CURRENT_STATE_PROJECT_KEY)
            if (!linqset) throw new Error("No organisation data")
            // the graph is rebuilt once for both, as they change together
            this.modelType = MODEL_TYPE.MODELS
            this.linqset = linqset
            this.organisationModels = [...linqset.models].sort((a, b) => a.name.localeCompare(b.name))
        }catch(err) {
            console.error("Error loading the organisation model for the introduction", err)
            this.snackBar.open("The example models couldn't be loaded. Please try again later.", "close")
            return
        }finally {
            this.loading = false
        }

        this.stage = INTRODUCTION_STAGE.ORGANISATION
        this.title = "How the business fits together"
        const messages = [
            `This is the organisation model for <b>${escapeHtml(this.organisation.name)}</b>. Each box is a model of one of its processes: the actions people and systems take, and the information they create and use. The number beside it is the hours a year spent on that process.`,
            'The lines show where information made in one process is used in another, so together the models show how the whole business runs, and how a change in one process reaches the others.',
            this.organisationModels.length
                ? '<b>Double-click a model</b> to see how it works, step by step, or choose one below.'
                : 'There are no models to walk through yet. Create one, and come back to step through it.'
        ]
        this.safeMessages = messages.map(m => this.sanitizer.bypassSecurityTrustHtml(m))
        if (this.leftDrawer) this.leftDrawer.open()
    }

    // a model double-clicked in the organisation model is walked through
    handleGraphDoubleClick(item:IModelItem) {
        if (this.stage !== INTRODUCTION_STAGE.ORGANISATION || !(item instanceof INode)) return
        const model = item.tag?.model as ModelDetails|undefined
        if (model?._key) this.chooseModel(model)
    }

    async chooseModel(model:ModelDetails) {
        if (this.navigating || !this.organisation) return
        this.navigating = true
        try {
            if (await this.loadModelData(this.organisation._key, model._key, CURRENT_STATE_PROJECT_KEY)) await this.beginModel()
        }finally {
            this.navigating = false
        }
    }

    // the graph is rebuilt for a model once the page next updates, so the walk through waits for it to be built
    handleGraphReady(linqset:Linqset) {
        this.graphBuilt.next(linqset)
    }

    private whenGraphShows(linqset:Linqset):Promise<void> {
        return new Promise(resolve => {
            const done = () => { subscription.unsubscribe(); window.clearTimeout(timer); resolve() }
            const subscription = this.graphBuilt.subscribe(built => { if (built === linqset) done() })
            // rather than wait for ever if it never says
            const timer = window.setTimeout(done, 15000)
        })
    }

    private async beginModel() {
        await this.graphShown
        await this.start()
    }

    // returns whether the model loaded; if it didn't, the visitor is told and nothing else runs on a missing model
    async loadModelData(organisationKey:string, modelKey:string, projectKey:string) {
        this.loading = true
        try {
            const l = await this.linqsetRepository.loadModelData(organisationKey, modelKey, projectKey, false)
            if (!l) throw new Error("No model data")
            l.layout = LAYOUTS.HIERARCHIC
            // set with the model, so the graph is rebuilt once for both
            this.modelType = MODEL_TYPE.PROCESS
            this.graphShown = this.whenGraphShows(l)
            this.linqset = l
            return true
        }catch(err) {
            console.error("Error loading model data for introduction layout", err)
            this.snackBar.open("That model couldn't be loaded. Please try another.", "close", {duration:5000})
            return false
        }finally {
            this.loading = false
        }
    }

    register() {
        const messages = ['That completes the model. Would you like to try <b>LINQ</b>?',
            '<b>LINQ</b> is free to use for an organisation with a single user and up to 150 nodes so you can see it for yourself without obligation.'
        ]
        const dialogRef = this.dialog.open(RegisterDialogComponent, {
            data: {model:this.linqset.model, messages},
            hasBackdrop:true,
            width: '500px',
            maxHeight: '90vh',     // Restricts outer container to 90% viewport height
            autoFocus: false       // Prevents unexpected jumps on layout calculation
        });

        // the dialog registers and only closes with the email once that worked; the registered page says where the email went
        dialogRef.afterClosed().subscribe((email:string|undefined) => {
            if (email) this.router.navigateByUrl(`registered`, {state:{email}});
        })
    }

    async restart() {
        const shown = this.linqset
        if (this.stage === INTRODUCTION_STAGE.PROJECT) {
            this.resetLinqset()
        }

        this.stage = INTRODUCTION_STAGE.INITIAL
        if (this.leftDrawer) this.leftDrawer.close()

        // the model may be changed next, so its report is built again
        this.epmLinqset = undefined
        this.epmSteps = []
        this.epmReady = false

        this.stopSimulationProgress()
        this.graphService.removeHeatmap(this.gcComponent.graphComponent)
        this.temporalService.clear()

        if (this.modelKey) {
            // the projects stage's model was put back, so the canvas rebuilds it from its binding; otherwise it is rebuilt here
            this.graphShown = this.linqset !== shown ? this.whenGraphShows(this.linqset) : this.gcComponent.newLinqset()
            await this.beginModel()
        }else {
            await this.showOrganisation()
        }
    }

    // goes back one step: to the previous action, or the stage before this one
    async back() {
        if (!this.canGoBack) return

        // from the first step, back to the organisation model to choose another
        if (this.stage === INTRODUCTION_STAGE.START) {
            await this.restart()
            return
        }

        // the projects stage is reached from the model as it is, so it can be shown again directly
        if (this.stage === INTRODUCTION_STAGE.FINISH && this.hasProjectStage) {
            await this.showProject()
            return
        }
        // the report stays built, so its parts, and the stages after it, go back to it without rebuilding the model. The
        // report covers the graph, so the projects stage's model can be put back under it while it shows
        if (this.stage === INTRODUCTION_STAGE.FINISH || this.stage === INTRODUCTION_STAGE.PROJECT) {
            if (this.stage === INTRODUCTION_STAGE.PROJECT) this.resetLinqset()
            await this.showEpm(Math.max(this.epmSteps.length - 1, 0))
            return
        }
        if (this.stage === INTRODUCTION_STAGE.EPM && this.epmReady && this.epmIndex > 0) {
            this.showEpmStep(this.epmIndex - 1)
            return
        }

        const walkthroughStage = (step:number) => step > 1 ? INTRODUCTION_STAGE.NEXT : INTRODUCTION_STAGE.START
        switch (this.stage) {
            case INTRODUCTION_STAGE.NEXT:
                await this.replayTo(walkthroughStage(this.step-1), this.step-1)
                break
            case INTRODUCTION_STAGE.ANIMATION:
                await this.replayTo(walkthroughStage(this.step), this.step)
                break
            case INTRODUCTION_STAGE.HEATMAP:
                await this.replayTo(INTRODUCTION_STAGE.ANIMATION, this.step)
                break
            case INTRODUCTION_STAGE.EPM:
                await this.replayTo(INTRODUCTION_STAGE.HEATMAP, this.step)
                break
        }
    }

    // the walk through builds on each step, so an earlier stage is reached by rebuilding the model and stepping through to it
    private async replayTo(stage:INTRODUCTION_STAGE, step:number) {
        this.navigating = true
        this.stopSimulationProgress()
        this.graphService.removeHeatmap(this.gcComponent.graphComponent)
        this.temporalService.clear()

        this.replaying = true
        try {
            await this.gcComponent.newLinqset()
            await this.start()
            // a guard, in case a stage doesn't move on
            let remaining = 1000
            while (this.isWalkthrough && this.step < step && remaining-- > 0) await this.loadNextStage()
            while (this.stage !== stage && remaining-- > 0) {
                // skipping to the end from part way through is how the simulation was reached
                if (this.isWalkthrough) await this.completeModel()
                else await this.loadNextStage()
            }
        }finally {
            this.replaying = false
            this.navigating = false
        }

        if (this.isWalkthrough) await this.alignModel(this.focusNode)
    }

    async alignModel(node:INode) {
        this.focusNode = node
        if (this.replaying) return

        this.filteredGraph.nodePredicateChanged()
        this.filteredGraph.edgePredicateChanged()

        const {graphComponent} = this.gcComponent

        await graphComponent.applyLayoutAnimated(new HierarchicalLayout({layoutOrientation: this.orientation}))

        if (!node) return

        const targetCenter = node.layout.center
        await graphComponent.zoomToAnimated(1 , targetCenter) // 1.0 is the zoom level

    }

    // create list of starting action nodes
    async start() {
        if (!this.gcComponent) return
        const {graphComponent} = this.gcComponent
        if (this.leftDrawer) this.leftDrawer.open()

        this.labelService.removeLabelsExceptName(graphComponent.graph)

        // set this up for insights later
        this.linqset.filter.fields = [INSIGHTS_FIELD.ANNUAL_HOURS, INSIGHTS_FIELD.AUTOMATED_ACTIONS, INSIGHTS_FIELD.CAPACITY, INSIGHTS_FIELD.CARBON, INSIGHTS_FIELD.COST, INSIGHTS_FIELD.PROCESSED, INSIGHTS_FIELD.REVENUE, INSIGHTS_FIELD.WORKLOAD]

        this.stage = INTRODUCTION_STAGE.START
        this.step = 1
        this.hasProjectStage = this.canShowProject()

        this.longestPathEdges = new LongestPath({
            subgraphNodes: (node) => {return node.tag?.node && isSupplyChain(node.tag.node.superType)}
        }).run(this.fullGraph).edges

        this.inputs = []
        this.title = "Where the model begins"

        this.fullGraph.edges.forEach(e => {
            if (!e.tag) e.tag = {filtered:true}
            else e.tag.filtered = true
        })

        const pathActions = new Set<INode>()
        this.longestPathEdges.forEach(e => [e.sourceNode, e.targetNode].forEach(n => {
            if (n.tag?.node?.superType === NODE_SUPERTYPE.ACTION) pathActions.add(n)
        }))
        let startsWithInput = false

        let currentMessages = []
        this.currentNode = undefined
        let previous:INode

        graphComponent.highlights.clear()

        this.fullGraph.nodes.forEach(n => {
            if (n.tag?.filtered === undefined) n.tag.filtered = true
            if (n.tag?.node) {
                const i = getInsights(n.tag.node, this.linqset)
                if (i?.isInput) {
                    this.inputs.push({node:n, itemsPerYear:i.numberProcessed})
                    const edge = this.longestPathEdges.find(e => e.tag?.edge?.sourceKey === n.tag.node._key)

                    if (edge) {
                        n.tag.filtered = false

                        if (n.tag.node.superType === NODE_SUPERTYPE.ACTION) {
                            this.currentNode = n
                            previous = this.currentNode
                            currentMessages = [`The model begins with the action ${this.nodeName(n)}.`]
                            currentMessages = currentMessages.concat(this.getCurrentNodeMessages())
                        }else {
                            startsWithInput = true
                            this.fullGraph.edgesAt(n, AdjacencyTypes.INCOMING).forEach(e => {
                                currentMessages.push(`The model receives ${this.nodeName(n)} as an input from the model ${escapeHtml(e.sourceNode.tag?.model?.name)}.`)
                                this.showFiltered(e, true, false)
                            })
                            this.currentNode = edge.targetNode
                            previous = this.currentNode
                        }

                        graphComponent.highlights.add(n)
                        this.showFiltered(edge, true)
                    }

                }
            }
        })
        // an input from another model gets a step of its own before its first action
        this.totalSteps = pathActions.size + (startsWithInput ? 1 : 0)
        currentMessages.unshift('The guide follows the main flow of information through the model, one action at a time, from where information comes in.')
        this.safeMessages = currentMessages.map(m => this.sanitizer.bypassSecurityTrustHtml(m))

        await this.alignModel(previous)
    }

    nodeName(node:INode) {
        const color = getColourForSuperType(node.tag.node.superType)
        return `<span class='node-name' style='--node-colour:${color}'>${escapeHtml(node.tag.node.name)}</span>`
    }

    showFiltered(e:IEdge, addHighlight:boolean=true, forward:boolean=true) {
        if (e.tag) e.tag.filtered = false
        else e.tag = {filtered:false}

        const n = forward?e.targetNode:e.sourceNode
        if (n.tag) n.tag.filtered = false
        else n.tag = {filtered:false}

        if (addHighlight) {
            this.gcComponent.graphComponent.highlights.add(e)
            this.gcComponent.graphComponent.highlights.add(n)
        }
        this.gcComponent.graphComponent.graphModelManager.nodeLabelGroup.above(this.gcComponent.graphComponent.renderTree.highlightGroup)
    }

    async loadNextStage() {
        if (this.stage === INTRODUCTION_STAGE.PROJECT) {
            this.resetLinqset()
        }

        if (this.stage === INTRODUCTION_STAGE.START) {
            this.stage = INTRODUCTION_STAGE.NEXT
        }else if (this.stage === INTRODUCTION_STAGE.ANIMATION) {
            this.stopAnimation()
            return
        }else if (this.stage === INTRODUCTION_STAGE.HEATMAP) {
            this.showEpm()
            return
        }else if (this.stage === INTRODUCTION_STAGE.EPM) {
            // the report is still being built
            if (!this.epmReady) return
            if (this.epmIndex < this.epmSteps.length - 1) this.showEpmStep(this.epmIndex + 1)
            else this.showProject()
            return
        }else if (this.stage === INTRODUCTION_STAGE.PROJECT) {
            this.returnToModel()
            return
        }

        if (this.stage === INTRODUCTION_STAGE.NEXT) {
            if (!this.currentNode?.tag?.node) {
                this.completeModel()
                return
            }

            const previous = this.currentNode
            this.step++
            this.title = this.currentNode.tag.node.name
            const messages = this.getCurrentNodeMessages()
            if (messages.length === 0) {
                this.completeModel()
                return
            }
            this.safeMessages = messages.map(m => this.sanitizer.bypassSecurityTrustHtml(m))

            await this.alignModel(previous)
        }
    }

    getCurrentNodeMessages() {
        const messages = []
        const {graphComponent} = this.gcComponent

        graphComponent.highlights.clear()
        this.currentNode.tag.filtered = false
        this.gcComponent.graphComponent.highlights.add(this.currentNode)

        const roles = []
        const systems = []

        this.fullGraph.edgesAt(this.currentNode, AdjacencyTypes.INCOMING)
            .filter(incoming => incoming.tag?.edge?.type === EDGE_TYPE.AUTOMATE || incoming.tag?.edge?.type === EDGE_TYPE.PERFORM).forEach(incoming => {
                this.showFiltered(incoming, true, false)
                if (incoming.sourceNode.tag.node.superType === NODE_SUPERTYPE.ROLE) {
                    const useby = this.fullGraph.edgesAt(incoming.sourceNode, AdjacencyTypes.INCOMING).filter(incoming2 => incoming2.tag?.edge?.type === EDGE_TYPE.USEBY)
                    if (useby.size === 0) {
                        roles.push(`${this.nodeName(incoming.sourceNode)}`)
                    }
                    useby.forEach(incoming2 => {
                        this.showFiltered(incoming2, true, false)
                        roles.push(`${this.nodeName(incoming.sourceNode)} using the system ${this.nodeName(incoming2.sourceNode)}`)
                    })
                }else {
                    systems.push(`${this.nodeName(incoming.sourceNode)}`)
                }
        })

        if (roles.length === 1) {
            messages.push(`The action ${this.nodeName(this.currentNode)} is performed by the people that have the role ${roles[0]}.`)
        }else if (roles.length > 1) {
            let m = `The action ${this.nodeName(this.currentNode)} is performed by each of the following roles concurrently: <ul>`
            roles.forEach(r => {
                m += `<li>${r}</li>`
            })
            messages.push(m + '</ul>')
        }

        if (systems.length === 1) {
            messages.push(`The action ${this.nodeName(this.currentNode)} is executed automatically (automated) by the system ${systems[0]}.`)
        }else if (systems.length > 1) {
            let m = `The action ${this.nodeName(this.currentNode)} is executed automatically (automated) by the systems: <ul>`
            systems.forEach(s => {
                m += `<li>${s}</li>`
            })
            messages.push(m + '</ul>')
        }

        const edges = this.fullGraph.edgesAt(this.currentNode, AdjacencyTypes.OUTGOING)
        this.currentNode = undefined

        if (edges.size === 0) return messages

        edges.forEach(e => {

            if (this.longestPathEdges.find(le => le === e) ) {
                const supply = this.fullGraph.edgesAt(e.targetNode, AdjacencyTypes.INCOMING).find(e => e.tag?.edge?.type === EDGE_TYPE.SUPPLY)
                if (supply) {
                    this.showFiltered(supply, true, false)
                }

                const system = supply?`, which is supplied by the system ${this.nodeName(supply.sourceNode)}`:''
                this.showFiltered(e, true, true)

                messages.push(`The action creates ${this.nodeName(e.targetNode)}${system}.`)
                this.fullGraph.edgesAt(e.targetNode, AdjacencyTypes.OUTGOING).forEach(e2 => {
                    this.showFiltered(e2, false, true)
                    if (e2.targetNode?.tag?.node?.superType === NODE_SUPERTYPE.OUTCOMES) {
                        messages.push(`The action creates the business outcome ${this.nodeName(e2.targetNode)}.`)
                    }else if (this.longestPathEdges.find(le => le === e2) ) {
                        this.currentNode = e2.targetNode
                    }
                })

            }
        })
        return messages
    }

    handleStageEvent(event:string) {
        if (event === "next") {
            this.loadNextStage()
        }else if (event === "finish") {
            this.completeModel()
            this.currentNode = undefined
        }
    }

    async completeModel() {
        this.gcComponent.graphComponent.highlights.clear()

        this.fullGraph.nodes.forEach(n => n.tag.filtered=false)
        this.fullGraph.edges.forEach(e => e.tag.filtered=false)
        this.title = undefined
        this.filteredGraph.nodePredicateChanged()
        this.filteredGraph.edgePredicateChanged()

        this.labelService.updateLabelsForGraph(this.fullGraph, false, this.linqset, INSIGHTS_FIELD.ANNUAL_HOURS)
        await this.gcComponent.graphComponent.applyLayoutAnimated(new HierarchicalLayout({layoutOrientation: this.orientation}), this.replaying ? TimeSpan.ZERO : undefined)

        this.startAnimation()
        this.startSimulationProgress()

        this.title = "Simulating the load"
        const messages = [
            `An animation of load through the model is now running. This highlights where queues form over time due to capacity constraints and how that impacts throughput in the model.`
        ]
        this.stage = INTRODUCTION_STAGE.ANIMATION

        this.safeMessages = messages.map(m => this.sanitizer.bypassSecurityTrustHtml(m))
    }

    stopAnimation() {
        this.stage = INTRODUCTION_STAGE.HEATMAP
        this.stopSimulationProgress()
        this.temporalService.clear()
        this.graphService.toggleHeatmap(this.gcComponent.graphComponent, this.linqset)
        this.title = "Where the time goes"
        const messages = ["A heatmap has been applied to the model to highlight where the most time is spent. It can also be applied to items processed, costs, carbon, capacity etc."]
        this.safeMessages = messages.map(m => this.sanitizer.bypassSecurityTrustHtml(m))
    }

    startAnimation() {
        this.temporalService.runAnimation(this.gcComponent.graphComponent, 5, this.linqset, this.inputs, [])
    }

    // the simulation runs on its own timer, so its progress is read every half second while it shows
    private startSimulationProgress() {
        this.stopSimulationProgress()
        this.refreshSimulationProgress()
        this.simulationTimer = window.setInterval(() => this.refreshSimulationProgress(), 500)
    }

    private stopSimulationProgress() {
        window.clearInterval(this.simulationTimer)
        this.simulationTimer = undefined
        this.simulation = undefined
    }

    private refreshSimulationProgress() {
        if (!this.temporalService.hasSimulation) return
        // a working day takes over a minute to simulate, so the time of day is shown for it to be seen moving
        const {received, completed, queued} = this.temporalService.progress
        const elapsed = Math.max(0, this.temporalService.tickNumber-1)*this.temporalService.secondsPerTick
        const intoDay = elapsed % WORKING_SECONDS_PER_DAY
        const clock = 9*3600 + intoDay           // the working day is shown starting at 9am
        const time = `${String(Math.floor(clock/3600)).padStart(2, '0')}:${String(Math.floor(clock%3600/60)).padStart(2, '0')}`
        this.simulation = {
            day: Math.floor(elapsed/WORKING_SECONDS_PER_DAY) + 1,
            days: Math.round(SECONDS_PER_YEAR/WORKING_SECONDS_PER_DAY),
            time,
            percent: Math.min(100, elapsed/SECONDS_PER_YEAR*100),
            received, completed, queued
        }
        this.simulationRunning = this.temporalService.isRunning
        this.simulationFinished = this.temporalService.isFinished
    }

    toggleSimulation() {
        if (this.temporalService.isRunning) this.temporalService.stopAnimation()
        else this.temporalService.continueAnimation()
        this.refreshSimulationProgress()
    }

    async showProject() {
        this.graphService.removeHeatmap(this.gcComponent.graphComponent)

        if (this.canShowProject()) {
            if (this.comparisonKey === CURRENT_STATE_PROJECT_KEY) {
                // linqset is a project
                this.comparison = this.linqsetRepository.comparison
                this.linqset.addRemovedNodesToLinqset(this.comparison)
            }else {
                // linqset is current state so get project and swicth them
                const graphData = await this.linqsetRepository.loadModelGraph(this.linqset.organisation._key, this.linqset.modelKey, this.comparisonKey)
                const orgData = await this.linqsetRepository.loadModelGraph(this.linqset.organisation._key, ORGANISATION_MODEL_KEY, this.comparisonKey)
                const filter = createInsightsFilter(this.comparisonKey)
                filter.modelKeys = [this.linqset.modelKey]
                this.comparison = this.linqset

                this.linqset = createLinqset(this.linqset.organisation, filter, graphData, orgData.nodes)
                this.linqset.addRemovedNodesToLinqset(this.comparison)
            }
            this.stage = INTRODUCTION_STAGE.PROJECT

            this.title = "Current state vs future state"
            const messages = ['After creating a model of current state, you can create a project to model future state changes and compare the two. Nodes and edges that have been removed in the future state are shown greyed out, red icons show a reduction in hours processing. The insights shown below show the impact of change.'
            ]
            this.safeMessages = messages.map(m => this.sanitizer.bypassSecurityTrustHtml(m))
        }else this.returnToModel()
    }

    // shows the EPM report in place of the graph and steps through its parts, from the one given. The report is built
    // the first time it is reached; until it is, Next waits
    async showEpm(index:number = 0) {
        this.stage = INTRODUCTION_STAGE.EPM
        // the heatmap would otherwise still be on the graph when the report gives way to the projects stage
        this.graphService.removeHeatmap(this.gcComponent.graphComponent)
        if (!this.epmLinqset) this.epmLinqset = this.linqset

        if (this.epmReady) {
            this.showEpmStep(Math.min(index, this.epmSteps.length - 1))
            return
        }

        this.title = "Building the EPM report"
        this.safeMessages = ["LINQ is scoring the model's economics: the value it creates, what it costs and where value is lost."].map(m => this.sanitizer.bypassSecurityTrustHtml(m))

        const steps = await this.waitForEpmReport()
        if (this.stage !== INTRODUCTION_STAGE.EPM) return
        if (!steps.length) {
            // the report couldn't be built, or has nothing to score, and says so itself
            this.title = "The EPM report"
            this.safeMessages = ["The EPM report scores the model's economics: the value it creates, what it costs and where value is lost."].map(m => this.sanitizer.bypassSecurityTrustHtml(m))
            return
        }
        this.epmSteps = steps
        this.epmReady = true
        this.showEpmStep(Math.min(index, steps.length - 1))
    }

    // the report loads its own data, so this waits for its parts to show, or for it to say it couldn't be built
    private waitForEpmReport():Promise<EpmTourStep[]> {
        return new Promise(resolve => {
            const started = Date.now()
            const check = () => {
                if (this.stage !== INTRODUCTION_STAGE.EPM) return resolve([])
                const root = this.epmReport?.nativeElement
                const built = root?.querySelector('[data-tour="headlines"]')
                // the status shown while it builds is role=status; an error or "nothing to score" isn't
                const failed = root?.querySelector('.scorecard-tab-status:not([role="status"])')
                if (built) return resolve(EPM_TOUR.filter(s => root.querySelector(`[data-tour="${s.anchor}"]`)))
                if (failed || Date.now() - started > 30000) return resolve([])
                window.setTimeout(check, 200)
            }
            check()
        })
    }

    // outlines a part of the report, brings it into view and explains it
    showEpmStep(index:number) {
        const step = this.epmSteps[index]
        if (!step) return
        this.epmIndex = index
        this.title = step.title
        this.safeMessages = [this.sanitizer.bypassSecurityTrustHtml(step.message)]

        const root = this.epmReport?.nativeElement
        root?.querySelectorAll('.tour-focus').forEach(e => e.classList.remove('tour-focus'))
        const element = root?.querySelector(`[data-tour="${step.anchor}"]`)
        if (element) {
            element.classList.add('tour-focus')
            element.scrollIntoView({behavior:'smooth', block:'start'})
        }
    }

    // whether there's a future state project, where the model changed, to compare with the model
    private canShowProject():boolean {
        return !!this.comparisonKey && this.comparisonKey !== this.linqset.workspaceKey && modelChangedInProject(this.linqset.model, this.comparisonKey)
    }

    returnToModel() {
        this.stage = INTRODUCTION_STAGE.FINISH

        this.title = "That's the tour"
        const messages = ['This concludes the introduction to the model. You can step through the model again and explore the insights below.']
        this.safeMessages = messages.map(m => this.sanitizer.bypassSecurityTrustHtml(m))
    }

    resetLinqset() {
        if (this.comparison) {
            if (this.comparisonKey !== CURRENT_STATE_PROJECT_KEY) {
                this.linqset = this.linqsetRepository.linqset
            }
            this.linqset.removeRemovedNodesToLinqset()
        }
    }

    public async handleFunctionEvent(event:{function:FUNCTION, value?:any}) {
        this.loading = true

        try {
            await this.functionService.executeFunction(event.function, event.value, this.linqset, this.linqset?.project, this.gcComponent.graphComponent, this.gcComponent)
        }catch(err) {
            console.error(err)
        }finally {
            this.loading = false
        }
    }
}